import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from 'contexts/auth';
import { useFiltersFromURL } from 'hooks/alerts/use-filters-from-url';
import { isInAlerts } from 'pages/alerts/utils';

import type {
  ContextualSearchType,
  UnifiedSearchMode,
  SearchOptionsType,
  SearchContextType,
  SearchType,
} from './searchContext.types';
import type { NavigateFunction } from 'react-router-dom';

export const SearchContext = createContext<undefined | SearchContextType>(
  undefined,
);

export const SearchProviderContext = createContext<
  undefined | ((search: SearchType) => void)
>(undefined);

const openSearchInV2 = (
  navigate: NavigateFunction,
  path: string,
  options: Pick<SearchOptionsType, 'shouldOpenInNewTab'> = {},
) =>
  options?.shouldOpenInNewTab ? window.open(path, '_blank') : navigate(path);

const searchFactory =
  (navigate: NavigateFunction) => (options?: SearchOptionsType) => {
    const { terms, tagId, shouldOpenInNewTab } = options || {};
    let path = terms ? `/search/${encodeURIComponent(terms)}` : '/search/';
    if (tagId) {
      path += '?tags=' + tagId;
    }
    openSearchInV2(navigate, path, { shouldOpenInNewTab });
  };

export const SearchProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const [searchCards, setSearchCards] = useState(() => searchFactory(navigate));
  const { setFiltersOnURL } = useFiltersFromURL();
  const { isDashboardAskKlueEnabled, isCurator, isWinLossExclusive } =
    useAuth();

  const [context, setContext] = useState<ContextualSearchType>(
    isInAlerts() ? 'alerts' : 'cards',
  );

  // Add unified search mode state - defaults to ask-klue if enabled, otherwise cards
  const [unifiedSearchMode, setUnifiedSearchMode] = useState<UnifiedSearchMode>(
    () => {
      return isDashboardAskKlueEnabled ? 'ask-klue' : 'cards';
    },
  );

  // Reset unified search mode when it becomes invalid
  useEffect(() => {
    // If current mode is ask-klue but it's no longer enabled, switch to cards
    if (unifiedSearchMode === 'ask-klue' && !isDashboardAskKlueEnabled) {
      setUnifiedSearchMode('cards');
    }
    // If current mode is cards but win-loss exclusive is enabled, switch to ask-klue if available
    if (unifiedSearchMode === 'cards' && isWinLossExclusive) {
      if (isDashboardAskKlueEnabled) {
        setUnifiedSearchMode('ask-klue');
      }
    }
    // If current mode is alerts but user is not curator, switch to cards or ask-klue
    if (unifiedSearchMode === 'alerts' && !isCurator) {
      setUnifiedSearchMode(isDashboardAskKlueEnabled ? 'ask-klue' : 'cards');
    }
  }, [
    unifiedSearchMode,
    isDashboardAskKlueEnabled,
    isCurator,
    isWinLossExclusive,
  ]);

  const provideSearch = useCallback(
    (search: SearchType) => {
      if (search) {
        setSearchCards(() => search);
      } else {
        setSearchCards(() => searchFactory(navigate));
      }
    },
    [navigate],
  );

  const searchAlerts = (options?: SearchOptionsType) => {
    if (isInAlerts()) {
      setFiltersOnURL(
        {
          add: {
            q: options?.terms,
            view: 'search',
          },
        },
        { forceRehydrate: true, shouldRehydrate: true, shouldFetch: true },
      );

      return;
    }

    navigate(`/alerts?q=${options?.terms}`);
  };

  const searchByContext = {
    cards: searchCards,
    alerts: searchAlerts,
  };

  return (
    <SearchContext.Provider
      value={{
        search: searchByContext[context],
        context,
        setContext,
        unifiedSearchMode,
        setUnifiedSearchMode,
      }}
    >
      <SearchProviderContext.Provider
        value={provideSearch}
        children={children}
      />
    </SearchContext.Provider>
  );
};

export const useSearch = () => {
  const search = useContext(SearchContext);
  if (search === undefined) {
    throw new Error('useSearch must be used within SearchProvider');
  }
  return search;
};

export const useProvideSearch = () => {
  const provideSearch = useContext(SearchProviderContext);
  if (provideSearch === undefined) {
    throw new Error('useProvideSearch must be used within SearchProvider');
  }
  return provideSearch;
};
